import type { Optional } from '@festina-life/core'
import { useSuspenseQuery } from '@tanstack/react-query'
import { jwtDecode } from 'jwt-decode'
import { useCallback } from 'react'

import { getEnvironmentSettings } from '../utility/getEnvironmentSettings'
import type { ServerComponent } from '../utility/types'
import { useFetchAppSettings } from './useFetchAppSettings'

export const useGetUserRoles = () => {
  const { data: appSettings } = useFetchAppSettings()
  const [environmentSettings] = getEnvironmentSettings(appSettings!)

  const getAllComponentsWithRoles = useCallback(() => {
    const componentAudience: ServerComponent<Optional<string>> = {
      life: environmentSettings.REACT_APP_LIFE_AUDIENCE,
      legalEntity: environmentSettings.REACT_APP_LEGALENTITY_AUDIENCE,
      moneyIn: environmentSettings.REACT_APP_MONEYIN_AUDIENCE,
      moneyOut: environmentSettings.REACT_APP_MONEYOUT_AUDIENCE,
      unitLink: environmentSettings.REACT_APP_UNITLINK_AUDIENCE,
      collCalc: environmentSettings.REACT_APP_COLLCALC_AUDIENCE,
      configuration: environmentSettings.REACT_APP_CONFIGURATION_AUDIENCE,
      dataMart: environmentSettings.REACT_APP_DATAMART_AUDIENCE,
      dataMart2: environmentSettings.REACT_APP_DATAMART2_AUDIENCE,
      legalEntityDataReplicator:
        environmentSettings.REACT_APP_LEGALENTITYDATAREPLICATOR_AUDIENCE,
      lifeDataReplicator:
        environmentSettings.REACT_APP_LIFEDATAREPLICATOR_AUDIENCE,
      unitLinkDataReplicator:
        environmentSettings.REACT_APP_UNITLINKDATAREPLICATOR_AUDIENCE,
    }

    const sessionStorageValues = Object.values(sessionStorage)
    const componentsWithRoles: ServerComponent<Set<string>> = {
      life: new Set(),
      legalEntity: new Set(),
      moneyIn: new Set(),
      moneyOut: new Set(),
      unitLink: new Set(),
      collCalc: new Set(),
      configuration: new Set(),
      dataMart: new Set(),
      dataMart2: new Set(),
      legalEntityDataReplicator: new Set(),
      lifeDataReplicator: new Set(),
      unitLinkDataReplicator: new Set(),
    }

    sessionStorageValues.forEach((cachedKey) => {
      if (typeof cachedKey !== 'string') {
        return
      }

      let parsedValue: { secret: string; target?: string } | undefined =
        undefined

      // The session storage key might not be parseable, therefor we just return if it doesn't match our expected format
      try {
        parsedValue = JSON.parse(cachedKey) as {
          secret: string
          target?: string
        }
      } catch {
        return
      }

      const audience = cachedKey ? parsedValue?.target : undefined
      Object.entries(componentAudience).forEach(([key, value]) => {
        if (value && value === audience && parsedValue) {
          const secret = parsedValue.secret
          componentsWithRoles[key as keyof typeof componentsWithRoles] =
            // Using jwtDecode here is needed as just using atob can crash due to the data that can be in a JWT token
            new Set((jwtDecode(secret) as { roles?: string[] })?.roles)
        }
      })
    })

    return componentsWithRoles
  }, [environmentSettings])

  return useSuspenseQuery({
    queryKey: ['allRoles'],
    queryFn: getAllComponentsWithRoles,
    staleTime: 'static',
  })
}
